import { Controller } from "stimulus";
import SupercastUploadManager from "packs/episode_upload_manager";

// Uploads a Sign-up Page's welcome video or audio straight to AWS through the background upload manager. The
// finished asset's id goes into the hidden field and is only attached to the page when the form is saved, so the
// Save buttons stay disabled until the upload is processed.
export default class extends Controller {
  static targets = ["file", "assetId", "status"];
  static values = { kind: String, mediaType: String, uploadsPath: String, uploadLabel: String, href: String, currentExternalId: String };

  connect() {
    this.unsubscribe = SupercastUploadManager.subscribe((event) => this.onManagerEvent(event));
    this.reattach();
  }

  // A Turbo visit back to this page builds a blank form while the manager keeps uploading, so pick the upload back up.
  reattach() {
    const upload = SupercastUploadManager.getAllUploads().reverse().find(
      (u) => u.kind === this.kindValue && u.uploadsPath === this.uploadsPathValue && u.state !== "error"
    );
    // A completed upload that is already the page's saved asset (we just saved and redirected) has nothing to re-attach.
    const alreadySaved = upload && upload.state === "completed" && upload.externalId === this.currentExternalIdValue;
    if (!upload || alreadySaved) {
      // A restored Turbo snapshot can carry stale markers and disabled Save buttons.
      this.element.removeAttribute("data-upload-unsaved");
      this.setPending(false);
      return;
    }

    this.localId = upload.localId;
    if (upload.state === "completed") {
      this.onManagerEvent({ type: "commit-success", upload });
      return;
    }

    this.setPending(true);
    this.statusTarget.textContent = upload.state === "uploading" || upload.state === "paused" ? "Uploading" : "Processing";
  }

  disconnect() {
    if (this.unsubscribe) this.unsubscribe();
  }

  choose() {
    this.fileTarget.click();
  }

  pick() {
    const file = this.fileTarget.files[0];
    if (!file) return;

    // Clear the input so re-choosing the same file after a failed upload still fires change.
    this.fileTarget.value = "";
    if (this.localId) SupercastUploadManager.cancel(this.localId);
    this.assetIdTarget.value = "";
    this.element.removeAttribute("data-upload-unsaved");
    this.setPending(true);
    this.statusTarget.textContent = "Uploading";
    this.localId = SupercastUploadManager.start({
      file,
      mediaType: this.mediaTypeValue,
      kind: this.kindValue,
      uploadsPath: this.uploadsPathValue,
      label: `${this.uploadLabelValue} · ${file.name}`,
      href: this.hrefValue,
    });
  }

  onManagerEvent({ type, upload }) {
    if (!upload || upload.localId !== this.localId) return;

    if (type === "progress") {
      this.statusTarget.textContent = `Uploading ${Math.floor((upload.bytesUploaded / upload.bytesTotal) * 100)}%`;
    } else if (type === "processing" || type === "transcoding") {
      this.statusTarget.textContent = "Processing";
    } else if (type === "commit-success") {
      this.assetIdTarget.value = upload.externalId;
      this.element.toggleAttribute("data-upload-unsaved", !!upload.externalId);
      this.statusTarget.textContent = "Uploaded. Save to publish.";
      this.setPending(false);
    } else if (type === "upload-error" || type === "commit-error") {
      this.statusTarget.textContent = upload.errorMessage || "Upload failed";
      this.setPending(false);
    }
  }

  // Save stays disabled while any slot's upload in the form (video or audio) is still pending.
  setPending(pending) {
    this.element.toggleAttribute("data-upload-pending", pending);

    const form = this.element.closest("form");
    if (!form) return;

    const busy = form.querySelector("[data-upload-pending]") !== null;
    form.querySelectorAll('[type="submit"]').forEach((button) => { button.disabled = busy; });
  }
}
